import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LogOut, Settings as SettingsIcon, Plus, Minus, Trash2, Check, X, Receipt, DollarSign, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente de Notificação
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-[#FF5500]';

  return (
    <div className={`notification-toast fixed top-4 left-1/2 transform -translate-x-1/2 z-50 ${bgColor} text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-down`}>
      {type === 'success' && <Check className="w-5 h-5" />}
      {type === 'error' && <X className="w-5 h-5" />}
      <span className="font-oswald font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Componente de impressão
const PrintTicket = ({ ticketData, onClose }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (ticketData && iframeRef.current) {
      const printValues = ticketData.print_values !== false;
      const isTab = ticketData.is_tab;
      
      const ticketHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            @page { size: 80mm auto; margin: 0; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; padding: 3mm; background: white; color: black; }
            .center { text-align: center; }
            .establishment { font-size: 16px; font-weight: bold; text-align: center; margin-bottom: 2mm; }
            .tab-number { font-size: 20px; font-weight: bold; text-align: center; margin: 3mm 0; border: 2px solid black; padding: 2mm; }
            .table-number { text-align: center; font-size: 14px; margin-bottom: 2mm; }
            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
            .info-line { margin: 1mm 0; }
            .item { margin: 1mm 0; }
            .item-detail { padding-left: 3mm; color: #333; }
            .total { font-size: 14px; font-weight: bold; margin: 2mm 0; }
            .payment { text-align: center; font-size: 12px; margin: 2mm 0; }
            .footer { text-align: center; margin-top: 3mm; }
          </style>
        </head>
        <body>
          <div class="establishment">${ticketData.establishment || 'ESTABELECIMENTO'}</div>
          ${isTab ? `<div class="tab-number">COMANDA ${String(ticketData.tab_number).padStart(2, '0')}</div>` : ''}
          ${ticketData.table_number ? `<div class="table-number">Mesa: ${ticketData.table_number}</div>` : ''}
          <div class="divider"></div>
          <div class="info-line">Data: ${ticketData.date} ${ticketData.time}</div>
          <div class="info-line">Vendedor: ${ticketData.seller || '-'}</div>
          <div class="divider"></div>
          ${ticketData.items.map(item => `
            <div class="item">
              <div>${item.name}</div>
              <div class="item-detail">
                ${printValues 
                  ? `${item.quantity}x ${ticketData.currency_symbol} ${item.price.toFixed(2)} = ${ticketData.currency_symbol} ${item.subtotal.toFixed(2)}`
                  : `Qtd: ${item.quantity}`
                }
              </div>
            </div>
          `).join('')}
          <div class="divider"></div>
          ${printValues ? `<div class="total center">TOTAL: ${ticketData.currency_symbol} ${ticketData.total.toFixed(2)}</div>` : ''}
          ${ticketData.payment_method ? `<div class="payment">Pagamento: ${ticketData.payment_method}</div>` : ''}
          <div class="divider"></div>
          <div class="footer">Obrigado!</div>
        </body>
        </html>
      `;

      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(ticketHtml);
      doc.close();

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          window.print();
        }
        setTimeout(onClose, 500);
      }, 200);
    }
  }, [ticketData, onClose]);

  if (!ticketData) return null;

  return (
    <iframe
      ref={iframeRef}
      style={{ position: 'fixed', left: '-9999px', top: 0, width: '80mm', height: '100%', border: 'none' }}
      title="print-frame"
    />
  );
};

// Modal de Pagamento
const PaymentModal = ({ isOpen, onClose, onConfirm, total, currencySymbol, paymentMethods }) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const methods = paymentMethods ? paymentMethods.split(',').map(m => m.trim()).filter(m => m) : [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#18181b] rounded-2xl w-full max-w-md p-6 border border-white/10">
        <h2 className="text-2xl font-oswald font-bold text-white mb-2">Forma de Pagamento</h2>
        <p className="text-white/60 mb-6">Total: <span className="text-[#FF5500] font-bold text-xl">{currencySymbol} {total.toFixed(2)}</span></p>
        
        <div className="grid grid-cols-1 gap-3 mb-6">
          {methods.map(method => (
            <button
              key={method}
              onClick={() => setSelectedMethod(method)}
              className={`p-4 rounded-xl font-oswald text-lg transition-all ${
                selectedMethod === method
                  ? 'bg-[#FF5500] text-white'
                  : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'
              }`}
            >
              {method}
            </button>
          ))}
        </div>
        
        <div className="flex gap-3">
          <Button onClick={onClose} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
            Cancelar
          </Button>
          <Button 
            onClick={() => selectedMethod && onConfirm(selectedMethod)} 
            disabled={!selectedMethod}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          >
            <Check className="w-4 h-4 mr-2" />
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Modal de Mesa
const TableModal = ({ isOpen, onClose, onConfirm }) => {
  const [tableNumber, setTableNumber] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#18181b] rounded-2xl w-full max-w-md p-6 border border-white/10">
        <h2 className="text-2xl font-oswald font-bold text-white mb-4">Número da Mesa</h2>
        
        <input
          type="text"
          value={tableNumber}
          onChange={(e) => setTableNumber(e.target.value)}
          placeholder="Ex: 1, A1, VIP..."
          className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white font-oswald text-xl text-center mb-6"
          autoFocus
        />
        
        <div className="flex gap-3">
          <Button onClick={() => onConfirm('')} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
            Pular
          </Button>
          <Button 
            onClick={() => onConfirm(tableNumber)} 
            className="flex-1 bg-[#FF5500] hover:bg-[#CC4400] text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
};

const Comandas = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [ticketToPrint, setTicketToPrint] = useState(null);
  const [showProducts, setShowProducts] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes, tabsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`),
        axios.get(`${API}/tabs`),
        axios.get(`${API}/settings`)
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      setTabs(tabsRes.data.filter(t => t.status === 'open'));
      setSettings(settingsRes.data);
      if (categoriesRes.data.length > 0) {
        setSelectedCategory(categoriesRes.data[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = () => {
    const curr = settings.currency || 'EUR';
    return {'BRL': 'R$', 'EUR': '€', 'USD': '$'}[curr] || '€';
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const handleNewTab = () => {
    if (settings.ask_table_number) {
      setShowTableModal(true);
    } else {
      createTab('');
    }
  };

  const createTab = async (tableNumber) => {
    try {
      const response = await axios.post(`${API}/tabs`, { table_number: tableNumber || null });
      setTabs([...tabs, response.data]);
      setSelectedTab(response.data);
      setShowTableModal(false);
      showNotification(`Comanda ${String(response.data.number).padStart(2, '0')} criada!`);
    } catch (error) {
      showNotification('Erro ao criar comanda', 'error');
    }
  };

  const selectTab = (tab) => {
    setSelectedTab(tab);
    setShowProducts(false);
  };

  const addToTab = async (product) => {
    if (!selectedTab) {
      showNotification('Selecione uma comanda primeiro', 'error');
      return;
    }

    try {
      const response = await axios.post(`${API}/tabs/${selectedTab.id}/items`, {
        product_id: product.id,
        name: product.name,
        quantity: 1,
        price: product.price
      });
      setSelectedTab(response.data);
      setTabs(tabs.map(t => t.id === response.data.id ? response.data : t));
    } catch (error) {
      showNotification('Erro ao adicionar item', 'error');
    }
  };

  const updateQuantity = async (productId, delta) => {
    if (!selectedTab) return;
    
    const item = selectedTab.items.find(i => i.product_id === productId);
    if (!item) return;

    const newQuantity = item.quantity + delta;
    
    try {
      if (newQuantity <= 0) {
        const response = await axios.delete(`${API}/tabs/${selectedTab.id}/items/${productId}`);
        setSelectedTab(response.data);
        setTabs(tabs.map(t => t.id === response.data.id ? response.data : t));
      } else {
        const response = await axios.put(`${API}/tabs/${selectedTab.id}/items/${productId}?quantity=${newQuantity}`);
        setSelectedTab(response.data);
        setTabs(tabs.map(t => t.id === response.data.id ? response.data : t));
      }
    } catch (error) {
      showNotification('Erro ao atualizar quantidade', 'error');
    }
  };

  const handleCloseTab = () => {
    if (!selectedTab || selectedTab.items.length === 0) {
      showNotification('Comanda vazia', 'error');
      return;
    }
    setShowPaymentModal(true);
  };

  const closeTab = async (paymentMethod) => {
    try {
      setShowPaymentModal(false);
      
      const closeResponse = await axios.post(`${API}/tabs/${selectedTab.id}/close`, {
        payment_method: paymentMethod
      });
      
      // Imprimir ticket
      const printResponse = await axios.post(`${API}/tabs/${selectedTab.id}/print`);
      if (printResponse.data.printer_type === 'windows' && printResponse.data.ticket_data) {
        printResponse.data.ticket_data.payment_method = paymentMethod;
        setTicketToPrint(printResponse.data.ticket_data);
      }
      
      showNotification(`Comanda ${String(selectedTab.number).padStart(2, '0')} fechada! ${paymentMethod}`);
      setTabs(tabs.filter(t => t.id !== selectedTab.id));
      setSelectedTab(null);
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Erro ao fechar comanda', 'error');
    }
  };

  const deleteTab = async () => {
    if (!selectedTab) return;
    if (!window.confirm(`Cancelar comanda ${String(selectedTab.number).padStart(2, '0')}?`)) return;

    try {
      await axios.delete(`${API}/tabs/${selectedTab.id}`);
      setTabs(tabs.filter(t => t.id !== selectedTab.id));
      setSelectedTab(null);
      showNotification('Comanda cancelada');
    } catch (error) {
      showNotification('Erro ao cancelar comanda', 'error');
    }
  };

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category_id === selectedCategory)
    : products;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-white font-oswald text-2xl">Carregando...</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print { .notification-toast, .no-print { display: none !important; } }
        @keyframes slide-down { from { transform: translate(-50%, -100%); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
      `}</style>

      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      {ticketToPrint && <PrintTicket ticketData={ticketToPrint} onClose={() => setTicketToPrint(null)} />}
      
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={closeTab}
        total={selectedTab?.total || 0}
        currencySymbol={getCurrencySymbol()}
        paymentMethods={settings.payment_methods}
      />
      
      <TableModal
        isOpen={showTableModal}
        onClose={() => setShowTableModal(false)}
        onConfirm={createTab}
      />

      <div className="flex flex-col h-screen bg-[#09090b] no-print">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-white/10">
          <div>
            <h1 className="text-2xl font-bold font-oswald text-white">Comandas</h1>
            <p className="text-white/60 font-inter text-sm">Olá, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button onClick={() => navigate('/admin')} className="bg-white/10 hover:bg-white/20 text-white border border-white/10">
                <SettingsIcon className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button onClick={() => { logout(); navigate('/login'); }} variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Painel Esquerdo - Cards de Comandas */}
          <div className="w-80 border-r border-white/10 flex flex-col bg-[#0a0a0a]">
            <div className="p-4 border-b border-white/10">
              <Button onClick={handleNewTab} className="w-full bg-[#FF5500] hover:bg-[#CC4400] text-white font-oswald text-lg h-12">
                <Plus className="w-5 h-5 mr-2" />
                Nova Comanda
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {tabs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-white/40">
                  <Receipt className="w-16 h-16 mb-4" />
                  <p className="font-inter text-center">Nenhuma comanda aberta</p>
                  <p className="text-sm text-center mt-1">Clique em Nova Comanda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => selectTab(tab)}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        selectedTab?.id === tab.id
                          ? 'bg-[#FF5500] text-white ring-2 ring-[#FF5500]/50'
                          : 'bg-[#18181b] text-white hover:bg-[#27272a] border border-white/5'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-2xl font-oswald font-bold">
                            {String(tab.number).padStart(2, '0')}
                          </span>
                          {tab.table_number && (
                            <span className="ml-2 text-sm opacity-70">
                              <Users className="w-3 h-3 inline mr-1" />
                              Mesa {tab.table_number}
                            </span>
                          )}
                        </div>
                        <span className={`font-oswald font-bold ${selectedTab?.id === tab.id ? 'text-white' : 'text-[#FF5500]'}`}>
                          {getCurrencySymbol()} {tab.total.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm opacity-70 mt-1">
                        {tab.items.length} {tab.items.length === 1 ? 'item' : 'itens'}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Painel Central - Detalhes da Comanda */}
          <div className="flex-1 flex flex-col">
            {selectedTab ? (
              <>
                {/* Header da Comanda */}
                <div className="p-4 border-b border-white/10 bg-[#18181b] flex justify-between items-center">
                  <div>
                    <h2 className="text-3xl font-oswald font-bold text-[#FF5500]">
                      Comanda {String(selectedTab.number).padStart(2, '0')}
                    </h2>
                    <div className="flex items-center gap-4 text-white/60 text-sm font-inter mt-1">
                      <span>{selectedTab.items.length} itens</span>
                      {selectedTab.table_number && (
                        <span className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          Mesa {selectedTab.table_number}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => setShowProducts(!showProducts)} className="bg-white/10 hover:bg-white/20 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    {showProducts ? 'Ver Comanda' : 'Adicionar Itens'}
                  </Button>
                </div>

                {/* Lista de Itens ou Produtos */}
                {showProducts ? (
                  <div className="flex-1 overflow-y-auto p-4 bg-[#0a0a0a]">
                    {/* Categorias */}
                    <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`px-4 py-2 rounded-full font-oswald whitespace-nowrap text-sm ${
                            selectedCategory === cat.id
                              ? 'bg-[#FF5500] text-white'
                              : 'bg-[#18181b] text-white/70 border border-white/5'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                    
                    {/* Grid de Produtos */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {filteredProducts.map(product => (
                        <div
                          key={product.id}
                          onClick={() => addToTab(product)}
                          className="p-4 rounded-xl bg-[#18181b] border border-white/5 hover:border-[#FF5500]/50 cursor-pointer transition-all active:scale-95"
                        >
                          <h3 className="font-oswald font-semibold text-white truncate">{product.name}</h3>
                          <p className="text-[#FF5500] font-oswald font-bold mt-1">{getCurrencySymbol()} {product.price.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto p-4">
                    {selectedTab.items.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-white/40">
                        <Receipt className="w-16 h-16 mb-4" />
                        <p className="font-inter">Comanda vazia</p>
                        <p className="text-sm">Clique em Adicionar Itens para começar</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedTab.items.map(item => (
                          <div key={item.product_id} className="flex justify-between items-center p-4 rounded-xl bg-[#18181b] border border-white/5">
                            <div className="flex-1">
                              <h4 className="font-oswald font-medium text-white">{item.name}</h4>
                              <p className="text-white/60 font-inter text-sm">{getCurrencySymbol()} {item.price.toFixed(2)} cada</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => updateQuantity(item.product_id, -1)}
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                              >
                                <Minus className="w-4 h-4 text-white" />
                              </button>
                              <span className="w-8 text-center font-oswald font-bold text-white text-xl">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.product_id, 1)}
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                              >
                                <Plus className="w-4 h-4 text-white" />
                              </button>
                              <span className="w-24 text-right font-oswald font-bold text-[#FF5500]">
                                {getCurrencySymbol()} {(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Footer da Comanda */}
                <div className="p-4 border-t border-white/10 bg-[#18181b]">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-oswald text-xl text-white">TOTAL</span>
                    <span className="font-oswald text-3xl font-bold text-[#FF5500]">
                      {getCurrencySymbol()} {selectedTab.total.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={deleteTab} variant="outline" className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                    <Button onClick={handleCloseTab} disabled={selectedTab.items.length === 0} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-oswald text-lg">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Fechar Comanda
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-white/40">
                <Receipt className="w-24 h-24 mb-4" />
                <p className="font-oswald text-2xl mb-2">Nenhuma comanda selecionada</p>
                <p className="font-inter">Selecione uma comanda ou crie uma nova</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Comandas;
